import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  IconButton,
  MenuItem,
  Radio,
  RadioGroup,
  Snackbar,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import PublicIcon from '@mui/icons-material/Public';
import Header from '../../components/Header';
import FeedbackOptionsEditor, { optionsToStrings, stringsToOptionRows } from '../../components/FeedbackOptionsEditor';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import { useTheme } from '@emotion/react';
import { tokens } from '../../theme';

const MCQ_TYPES = ['1', '4'];

const FeedbackDetails = () => {
  const { id } = useParams();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();
  const [openToast, setOpenToast] = useState(false);
  const [toastMessage, setToastmessage] = useState('');

  const token = localStorage.getItem('token');

  const [isGlobal, setIsGlobal] = useState(false);
  const [values, setValues] = useState({
    question: '',
    tooltip: '',
    questionType: '',
    ansOptions: '',
    defaultValue: '',
    countryId: '',
    departureDate: null,
    isGuideQuestion: false,
  });

  const [options, setOptions] = useState(stringsToOptionRows([]));
  const [country, setCountry] = useState([]);
  const [trips, setTrips] = useState([]);

  const getCountryNames = async () => {
    const res = await fetch(`${process.env.REACT_APP_APILINK}/countrynames`, { headers: { token } });
    const data = await res.json();
    setCountry(data);
  };

  const getTripsByDestination = async (countryId) => {
    if (!countryId) { setTrips([]); return; }
    try {
      const res = await fetch(
        `${process.env.REACT_APP_APILINK}/admin/feedback/trips-by-destination?countryId=${countryId}`,
        { headers: { token } }
      );
      const data = await res.json();
      if (data.success) setTrips(data.data || []);
    } catch (e) { setTrips([]); }
  };

  useEffect(() => {
    getCountryNames();
  }, []);

  useEffect(() => {
    const getFeedbackDetails = async () => {
      const res = await fetch(`${process.env.REACT_APP_APILINK}/admin/feedback/${id}`, { headers: { token } });
      const data = await res.json();
      if (data.success) {
        setValues(data.feedbackQuestion);
        setOptions(stringsToOptionRows(data.feedbackQuestion.ansOptions || []));
        setIsGlobal(!data.feedbackQuestion.countryId);
        if (data.feedbackQuestion.countryId) {
          getTripsByDestination(data.feedbackQuestion.countryId);
        }
      }
    };
    getFeedbackDetails();
  }, [id]);

  const handleChange = (e) => {
    setValues({ ...values, [e.target.name]: e.target.value });
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    if (!values.question || !values.tooltip || !values.questionType) {
      setOpenToast(true);
      setToastmessage('Please fill all details');
      return;
    }

    const ansOptions = optionsToStrings(options);
    if (MCQ_TYPES.includes(String(values.questionType)) && ansOptions.length === 0) {
      setOpenToast(true);
      setToastmessage('Please add at least one answer option');
      return;
    }

    try {
      const res = await fetch(`${process.env.REACT_APP_APILINK}/admin/feedback/${id}`, {
        method: 'PUT',
        headers: {
          'Content-type': 'application/json',
          token,
        },
        body: JSON.stringify({
          ...values,
          ansOptions,
          countryId: isGlobal ? null : values.countryId,
          departureDate: isGlobal ? null : values.departureDate,
        }),
      });
      const data = await res.json();
      if (data.message) {
        setOpenToast(true);
        setToastmessage(data.message);
        if (data.success) {
          setTimeout(() => {
            navigate('/feedback');
          }, 2000);
        }
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleCloseToast = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenToast(false);
  };

  const action = (
    <>
      <IconButton size="small" aria-label="close" color="inherit" onClick={handleCloseToast}>
        <CloseIcon fontSize="small" />
      </IconButton>
    </>
  );

  const showOptions = MCQ_TYPES.includes(String(values.questionType));

  return (
    <Box m="20px">
      <Box display="flex" justifyContent="space-between" alignItems="center" rowGap="10px" flexWrap="wrap-reverse">
        <Header title="FEEDBACK QUESTION" subtitle="Edit the Feedback Question" />

        <Box>
          <Link to={-1}>
            <Button
              sx={{
                backgroundColor: colors.blueAccent[700],
                color: colors.grey[100],
                fontSize: '14px',
                fontWeight: 'bold',
                padding: '10px 20px',
                '&:hover': {
                  backgroundColor: colors.blueAccent[800],
                },
              }}
            >
              Go Back
            </Button>
          </Link>
        </Box>
      </Box>

      <form onSubmit={handleFormSubmit}>
        <Box
          style={{
            display: 'grid',
            gap: '10px',
          }}
        >
          <TextField
            fullWidth
            variant="filled"
            type="text"
            label="Question"
            onChange={handleChange}
            name="question"
            value={values.question}
            required
          />

          <TextField
            fullWidth
            variant="filled"
            type="text"
            label="Tooltip"
            onChange={handleChange}
            name="tooltip"
            value={values.tooltip}
            required
          />

          <FormControl component="fieldset">
            <Typography variant="h5" mt="10px" color={colors.grey[100]}>
              Question Type
            </Typography>
            <RadioGroup
              aria-label="questionType"
              name="questionType"
              value={values.questionType}
              onChange={(e) => {
                handleChange(e);
                if (!MCQ_TYPES.includes(String(e.target.value))) {
                  setOptions(stringsToOptionRows([]));
                }
              }}
              row
            >
              <FormControlLabel value={1} control={<Radio color="success" />} label="Single Choice (MCQ)" />
              <FormControlLabel value={4} control={<Radio color="success" />} label="Multiple Choice" />
              <FormControlLabel value={2} control={<Radio color="success" />} label="Short Answer" />
              <FormControlLabel value={3} control={<Radio color="success" />} label="Rating" />
            </RadioGroup>
          </FormControl>

          {showOptions && <FeedbackOptionsEditor options={options} setOptions={setOptions} colors={colors} />}

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              p: 1.5,
              borderRadius: 1,
              border: `1px solid ${isGlobal ? '#1a6b3c' : colors.grey[700]}`,
              backgroundColor: isGlobal ? '#0d2e1a' : 'transparent',
            }}
          >
            <PublicIcon sx={{ color: isGlobal ? '#4caf50' : colors.grey[400] }} />
            <Box flex={1}>
              <Typography variant="body2" fontWeight={600} color={isGlobal ? '#4caf50' : colors.grey[200]}>
                Global / Standard Question
              </Typography>
              <Typography variant="caption" color={colors.grey[400]}>
                Auto-appears in ALL trips — no need to assign per trip
              </Typography>
            </Box>
            <Switch
              checked={isGlobal}
              onChange={(e) => {
                setIsGlobal(e.target.checked);
                if (e.target.checked) setValues((v) => ({ ...v, countryId: null }));
              }}
              color="success"
            />
          </Box>

          {!isGlobal && (
            <>
              <TextField
                fullWidth
                variant="filled"
                type="text"
                label="Destination"
                onChange={(e) => {
                  handleChange(e);
                  setValues((v) => ({ ...v, countryId: e.target.value, departureDate: null }));
                  getTripsByDestination(e.target.value);
                }}
                value={values.countryId || ''}
                name="countryId"
                select
              >
                {country &&
                  country.map((c) => (
                    <MenuItem key={c.CountryID} value={c.CountryID}>
                      {c.CountryName}
                    </MenuItem>
                  ))}
              </TextField>

              {values.countryId && trips.length > 0 && (
                <>
                  <TextField
                    fullWidth
                    variant="filled"
                    type="text"
                    label="Specific Trip (optional)"
                    onChange={(e) => setValues((v) => ({ ...v, departureDate: e.target.value }))}
                    value={values.departureDate || ''}
                    name="departureDate"
                    select
                  >
                    <MenuItem value="">All trips of this destination</MenuItem>
                    {trips.map((t) => (
                      <MenuItem key={t.departureDate} value={t.departureDate}>
                        {t.departureDate} {t.endDate ? `→ ${t.endDate}` : ''}
                      </MenuItem>
                    ))}
                  </TextField>
                  <Typography variant="body2" color={colors.grey[400]} fontStyle="italic">
                    *Leave blank to apply to all trips of selected destination
                  </Typography>
                </>
              )}
            </>
          )}

          <FormControlLabel
            control={
              <Checkbox
                color="success"
                checked={Boolean(values.isGuideQuestion)}
                onChange={(e) => setValues({ ...values, isGuideQuestion: e.target.checked })}
              />
            }
            label={
              <Typography variant="body2" color={colors.grey[100]}>
                Guide-specific question (used in Guide Performance report ratings)
              </Typography>
            }
          />

          <Button
            type="submit"
            sx={{
              backgroundColor: colors.blueAccent[700],
              color: colors.grey[100],
              width: 'fit-content',
              fontSize: '14px',
              fontWeight: 'bold',
              padding: '10px 30px',
              '&:hover': {
                backgroundColor: colors.blueAccent[800],
              },
            }}
          >
            Submit
          </Button>
        </Box>
      </form>

      <Snackbar
        open={openToast}
        autoHideDuration={5000}
        onClose={handleCloseToast}
        message={toastMessage}
        action={action}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      />
    </Box>
  );
};

export default FeedbackDetails;
